import { useEffect, useState } from "react";
import { apiRequest, ApiError } from "../../api/client";
import type { Language, Speaker } from "../../api/types";

export default function SpeakersPage() {
  const [speakers, setSpeakers] = useState<Speaker[] | null>(null);
  const [languages, setLanguages] = useState<Language[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({ name: "", organization: "", country: "", languageCode: "en" });

  async function load() {
    const [spk, langs] = await Promise.all([
      apiRequest<Speaker[]>("/speakers"),
      apiRequest<Language[]>("/languages", { auth: false }),
    ]);
    setSpeakers(spk);
    setLanguages(langs);
  }

  useEffect(() => {
    void load();
  }, []);

  async function createSpeaker() {
    setBusy(true);
    setError(null);
    try {
      await apiRequest("/speakers", {
        method: "POST",
        body: {
          name: form.name,
          organization: form.organization || null,
          country: form.country || null,
          languageCode: form.languageCode,
        },
      });
      setShowForm(false);
      setForm({ name: "", organization: "", country: "", languageCode: form.languageCode });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to create speaker");
    } finally {
      setBusy(false);
    }
  }

  async function removeSpeaker(id: string) {
    if (!confirm("Remove this speaker?")) return;
    await apiRequest(`/speakers/${id}`, { method: "DELETE" });
    await load();
  }

  return (
    <div>
      <div className="toolbar">
        <h1 style={{ margin: 0, flex: 1 }}>Speakers</h1>
        <button className="btn-primary" onClick={() => setShowForm(true)}>
          + New speaker
        </button>
      </div>

      {speakers === null && <p>Loading…</p>}
      {speakers && speakers.length === 0 && <p>No speakers yet.</p>}
      {speakers && speakers.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Organization</th>
              <th>Country</th>
              <th>Language</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {speakers.map((s) => (
              <tr key={s.id}>
                <td>{s.name}</td>
                <td>{s.organization ?? "—"}</td>
                <td>{s.country ?? "—"}</td>
                <td>{s.languageCode.toUpperCase()}</td>
                <td>
                  <button className="btn-ghost" onClick={() => removeSpeaker(s.id)}>
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showForm && (
        <div className="modal-backdrop" onClick={() => setShowForm(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>New speaker</h2>
            <div className="stack">
              <div className="field">
                <label>Name</label>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus />
              </div>
              <div className="grid-2">
                <div className="field">
                  <label>Organization</label>
                  <input
                    value={form.organization}
                    onChange={(e) => setForm({ ...form, organization: e.target.value })}
                  />
                </div>
                <div className="field">
                  <label>Country</label>
                  <input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} />
                </div>
              </div>
              <div className="field">
                <label>Speaking language</label>
                <select
                  value={form.languageCode}
                  onChange={(e) => setForm({ ...form, languageCode: e.target.value })}
                >
                  {languages.map((l) => (
                    <option key={l.code} value={l.code}>
                      {l.name}
                    </option>
                  ))}
                </select>
              </div>
              {error && <div className="error-banner">{error}</div>}
            </div>
            <div className="modal-actions">
              <button className="btn-ghost" onClick={() => setShowForm(false)}>
                Cancel
              </button>
              <button className="btn-primary" disabled={busy || !form.name} onClick={createSpeaker}>
                {busy ? "Creating…" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
